import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { CadExtractionStatus, CallDetails, ReviewComment } from '../../types/Call';
import { QACriteriaDisplay } from './QACriteriaDisplay';
import { ReviewComments } from './ReviewComments';
import { CadRecordTab } from './CadRecordTab';
import { describeAppliedFields } from './cadRecordDisplay';
import { useCadRecord } from '../../hooks/useCadRecord';
import { useCallContext } from '../../contexts/CallContext';
import { notificationService } from '../../services/notificationService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`qa-tabpanel-${index}`}
      aria-labelledby={`qa-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface TabbedQASectionProps {
  callDetails: CallDetails;
  reviewComments: ReviewComment[];
  onScoresUpdated?: (updatedCallDetails: CallDetails) => void;
  onAddComment: (comment: string) => void;
  onRegenerateAnalysis: () => Promise<void>;
  isAnalysisPolling: boolean;
  userRole: string;
  onCallRecordApplied: (operatorChanged: boolean) => Promise<void>;
}

/** Tab chip text for the record's reading stage, so a failed read never says "Attached". */
const cadChipLabel = (status: CadExtractionStatus) => {
  switch (status) {
    case 'Pending':
    case 'Extracting':
      return 'Reading';
    case 'ExtractionFailed':
      return 'Not read';
    case 'Extracted':
      return 'Attached';
  }
};

const CAD_TAB_INDEX = 2;

/**
 * Runs `onLanded` when `key` changes after the first read for a call: a write the sweep made
 * with no click on this page (an automatic apply, a rescore). The first read only records what is
 * there, since a change from an earlier visit is already in the call that loaded; `loaded` false
 * (the call changed) forgets the key so the next call starts fresh.
 */
function useLandedKey(loaded: boolean, key: string | undefined, onLanded: () => void) {
  const seenRef = useRef<{ key: string | undefined }>(undefined);
  useEffect(() => {
    if (!loaded) {
      seenRef.current = undefined;
      return;
    }
    const seen = seenRef.current;
    seenRef.current = { key };
    if (!seen || seen.key === key || !key) return;
    onLanded();
  }, [loaded, key, onLanded]);
}

export const TabbedQASection: React.FC<TabbedQASectionProps> = ({
  callDetails,
  reviewComments,
  onScoresUpdated,
  onAddComment,
  onRegenerateAnalysis,
  isAnalysisPolling,
  userRole,
  onCallRecordApplied,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  // Lives here, not in the tab, so the record and its poll survive switching tabs and the
  // tab label can show whether a record is attached.
  const cad = useCadRecord(callDetails.callId);
  // null: the agency has CAD records off; undefined: still checking. Either way, no tab.
  const showCadTab = Array.isArray(cad.attachments);
  const canEditCad = userRole === 'Supervisor' || userRole === 'TenantAdmin';
  // If the tab disappears while selected (the flag turned off mid-session), fall back to Analysis.
  const selectedTab = !showCadTab && activeTab === CAD_TAB_INDEX ? 0 : activeTab;
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // The assistant's CAD entry point (leg 5) works this same record: publish it while the tab
  // exists, and withdraw it when the flag is off or the page goes away. `cad` is one object per
  // distinct state, so this fires on a state change, not on every render.
  const { setCadChat } = useCallContext();
  useEffect(() => {
    if (!showCadTab) return;
    setCadChat({ cad, canEdit: canEditCad, onCallRecordApplied });
    return () => setCadChat(undefined);
  }, [showCadTab, cad, canEditCad, onCallRecordApplied, setCadChat]);

  // An automatic apply (leg 4c) changes the call record while the page is open, with no click to
  // refresh after it: say so, whichever tab is showing, and re-read the call, as the tab does
  // after a supervisor's apply.
  const loaded = cad.attachments !== undefined;
  const appliedFields = cad.record?.appliedFields;
  const onApplied = useCallback(() => {
    const fields = appliedFields ?? [];
    notificationService.info(
      'Call Record Updated',
      `Applied from the CAD record automatically: ${describeAppliedFields(fields)}.`
    );
    void onCallRecordApplied(fields.includes('OperatorId'));
  }, [appliedFields, onCallRecordApplied]);
  useLandedKey(loaded, cad.appliedAutomaticallyKey, onApplied);

  // A rescore lands from the sweep, after a click here, in the assistant, or after a
  // regeneration re-queued it (leg 6): the call's scores changed. Same shape: say so, and re-read
  // the call so the Analysis tab shows the new category and overall.
  const onRescored = useCallback(() => {
    notificationService.success(
      'CAD Skills Rescored',
      'The CAD Skills category was scored against the CAD record and the overall score updated.'
    );
    void onCallRecordApplied(false);
  }, [onCallRecordApplied]);
  useLandedKey(loaded, cad.rescoredKey, onRescored);

  // An analysis that just landed may have re-queued the rescore, and nothing on this page polls
  // the record for that; one re-read lets the hook see it and start its wait.
  const { refresh } = cad;
  const wasAnalysisPollingRef = useRef(isAnalysisPolling);
  useEffect(() => {
    if (wasAnalysisPollingRef.current && !isAnalysisPolling && showCadTab) refresh();
    wasAnalysisPollingRef.current = isAnalysisPolling;
  }, [isAnalysisPolling, showCadTab, refresh]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  // Regeneration needs an existing transcription to analyse. Stays rendered while polling —
  // it is disabled and shows a spinner, which is the running indicator for this control.
  const canRegenerate = !!callDetails.transcriptionText;

  return (
    <Paper sx={{ height: 'fit-content' }}>
      <Box p={3} borderBottom={1} borderColor="divider">
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minWidth: 0 }}
          >
            <Tab label="Analysis" id="qa-tab-0" aria-controls="qa-tabpanel-0" />
            <Tab label="Comments" id="qa-tab-1" aria-controls="qa-tabpanel-1" />
            {showCadTab && (
              <Tab
                id={`qa-tab-${CAD_TAB_INDEX}`}
                aria-controls={`qa-tabpanel-${CAD_TAB_INDEX}`}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>CAD Record</span>
                    {cad.record && (
                      <Chip
                        label={cadChipLabel(cad.record.extractionStatus)}
                        size="small"
                        color={cad.record.extractionStatus === 'ExtractionFailed' ? 'error' : 'primary'}
                        // Filled for the failure: outlined error text is under 3:1 on dark paper.
                        variant={cad.record.extractionStatus === 'ExtractionFailed' ? 'filled' : 'outlined'}
                      />
                    )}
                  </Box>
                }
              />
            )}
          </Tabs>
          {selectedTab === 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              {canRegenerate && (
                <Tooltip title={isAnalysisPolling ? 'AI analysis running' : 'Regenerate AI Analysis'}>
                  {/* span wrapper: a disabled button fires no events, so Tooltip needs a live child */}
                  <span>
                    <IconButton
                      size="small"
                      onClick={onRegenerateAnalysis}
                      disabled={isAnalysisPolling}
                      aria-label={isAnalysisPolling ? 'AI analysis running' : 'Regenerate analysis'}
                      sx={{ color: 'grey.600', '&:hover': { bgcolor: 'grey.100' } }}
                    >
                      {isAnalysisPolling ? <CircularProgress size={18} aria-hidden /> : <RefreshOutlined />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {/* Disabled while analysis runs: a save would write DetailedScores that the
                  finishing analysis then overwrites wholesale, losing the manual override. */}
              <Tooltip
                title={
                  isAnalysisPolling
                    ? 'Score editing is unavailable while AI analysis is running'
                    : 'Edit scores'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleEditClick}
                    disabled={isAnalysisPolling}
                    aria-label={
                      isAnalysisPolling
                        ? 'Edit scores, unavailable while analysis is running'
                        : 'Edit scores'
                    }
                    sx={{
                      color: 'grey.600',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <TabPanel value={selectedTab} index={0}>
        <QACriteriaDisplay
          callDetails={callDetails}
          onScoresUpdated={onScoresUpdated}
          onRegenerateAnalysis={onRegenerateAnalysis}
          isAnalysisPolling={isAnalysisPolling}
          editDialogOpen={editDialogOpen}
          setEditDialogOpen={setEditDialogOpen}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <ReviewComments
          comments={reviewComments}
          onAddComment={onAddComment}
        />
      </TabPanel>

      {showCadTab && (
        <TabPanel value={selectedTab} index={CAD_TAB_INDEX}>
          <CadRecordTab
            callDetails={callDetails}
            canEdit={canEditCad}
            cad={cad}
            onCallRecordApplied={onCallRecordApplied}
          />
        </TabPanel>
      )}
    </Paper>
  );
};